import { Order } from "../types";

const PAYMENT_LABELS: Record<string, string> = {
  CASH: "Наличные",
  CREDIT: "В долг",
  ADVANCE: "С аванса",
};

const unitLabel = (u?: string) =>
  u === "PIECE" ? "шт." : u === "METER" ? "м." : u === "KG" ? "кг." : (u ?? "");

const fmt = (n: number) =>
  n.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export function printInvoice(order: Order) {
  const items = order.items ?? [];
  const invoiceNumber = `СФ-${String(order.id).padStart(6, "0")}`;
  const date = new Date(order.createdAt).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const deliveredDate = new Date().toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const rowsHtml = items
    .map((item, i) => {
      const name = item.product?.name ?? `Товар #${item.productId}`;
      const unit = unitLabel(item.product?.unit);
      const qty = Number(item.quantity);
      const price = Number(item.unitPrice);
      const total = qty * price;
      return `
      <tr>
        <td class="tc">${i + 1}</td>
        <td>${name}</td>
        <td class="tc">${unit}</td>
        <td class="tr">${qty.toLocaleString("ru-RU", { maximumFractionDigits: 2 })}</td>
        <td class="tr">${fmt(price)}</td>
        <td class="tr total-cell">${fmt(total)}</td>
      </tr>`;
    })
    .join("");

  const totalAmount = Number(order.totalAmount);
  const paymentLabel = PAYMENT_LABELS[order.paymentType] ?? order.paymentType;

  // Amount in words (simplified)
  const amountInWords = rubles(totalAmount);

  const html = `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8" />
  <title>Счёт-фактура ${invoiceNumber}</title>
  <style>
    @page { size: A4; margin: 18mm 15mm; }

    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Arial', sans-serif;
      font-size: 11pt;
      color: #1a1a1a;
      line-height: 1.4;
    }

    /* ── Header ── */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 3px solid #1e40af;
      padding-bottom: 14px;
      margin-bottom: 20px;
    }

    .brand { display: flex; flex-direction: column; gap: 4px; }

    .brand-name {
      font-size: 22pt;
      font-weight: 800;
      color: #1e40af;
      letter-spacing: -0.5px;
    }

    .brand-sub {
      font-size: 9pt;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .invoice-meta {
      text-align: right;
    }

    .invoice-number {
      font-size: 16pt;
      font-weight: 700;
      color: #1e40af;
    }

    .invoice-label {
      font-size: 9pt;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }

    .invoice-dates {
      font-size: 9pt;
      color: #374151;
      margin-top: 4px;
    }

    /* ── Parties ── */
    .parties {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-bottom: 20px;
    }

    .party-box {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 12px 14px;
      background: #f9fafb;
    }

    .party-title {
      font-size: 8pt;
      font-weight: 700;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 4px;
    }

    .party-name {
      font-size: 12pt;
      font-weight: 700;
      color: #111827;
      margin-bottom: 4px;
    }

    .party-detail {
      font-size: 9pt;
      color: #6b7280;
      line-height: 1.6;
    }

    /* ── Info strip ── */
    .info-strip {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      margin-bottom: 20px;
    }

    .info-chip {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 8px 12px;
      background: #fff;
    }

    .info-chip-label {
      font-size: 8pt;
      color: #9ca3af;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin-bottom: 2px;
    }

    .info-chip-value {
      font-size: 10pt;
      font-weight: 600;
      color: #111827;
    }

    /* ── Table ── */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
      font-size: 10pt;
    }

    .items-table thead tr {
      background: #1e40af;
      color: #fff;
    }

    .items-table thead th {
      padding: 8px 10px;
      font-weight: 600;
      font-size: 9pt;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .items-table tbody tr:nth-child(even) {
      background: #f8faff;
    }

    .items-table tbody tr:last-child td {
      border-bottom: 2px solid #1e40af;
    }

    .items-table tbody td {
      padding: 8px 10px;
      border-bottom: 1px solid #e5e7eb;
    }

    .tc { text-align: center; }
    .tr { text-align: right; }

    .total-cell { font-weight: 600; }

    /* ── Totals ── */
    .totals-section {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 20px;
    }

    .totals-box {
      min-width: 280px;
    }

    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 5px 0;
      border-bottom: 1px solid #f3f4f6;
      font-size: 10pt;
    }

    .totals-row:last-child {
      border-bottom: none;
      border-top: 2px solid #1e40af;
      padding-top: 8px;
      margin-top: 4px;
      font-size: 13pt;
      font-weight: 700;
      color: #1e40af;
    }

    .totals-label { color: #6b7280; }

    /* ── Amount in words ── */
    .amount-words {
      background: #eff6ff;
      border-left: 4px solid #1e40af;
      border-radius: 0 6px 6px 0;
      padding: 10px 14px;
      font-size: 10pt;
      color: #1e3a8a;
      margin-bottom: 24px;
    }

    .amount-words span { font-weight: 600; }

    /* ── Signatures ── */
    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 30px;
      margin-top: 32px;
    }

    .sig-block { }

    .sig-title {
      font-size: 9pt;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      margin-bottom: 24px;
    }

    .sig-line {
      border-top: 1px solid #9ca3af;
      padding-top: 4px;
      font-size: 9pt;
      color: #374151;
    }

    /* ── Footer ── */
    .footer {
      margin-top: 30px;
      text-align: center;
      font-size: 8pt;
      color: #9ca3af;
      border-top: 1px solid #e5e7eb;
      padding-top: 10px;
    }

    /* ── Status badge ── */
    .status-delivered {
      display: inline-block;
      background: #dcfce7;
      color: #166534;
      border: 1px solid #86efac;
      border-radius: 4px;
      padding: 2px 8px;
      font-size: 9pt;
      font-weight: 600;
    }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>

  <!-- Print button (hidden when printing) -->
  <div class="no-print" style="text-align:right; margin-bottom: 12px;">
    <button onclick="window.print()" style="
      background:#1e40af; color:#fff; border:none; padding:10px 24px;
      border-radius:6px; font-size:14px; cursor:pointer; font-weight:600;
    ">🖨 Распечатать</button>
  </div>

  <!-- Header -->
  <div class="header">
    <div class="brand">
      <div class="brand-name">Wilsun</div>
      <div class="brand-sub">Сантехника и строительные материалы</div>
    </div>
    <div class="invoice-meta">
      <div class="invoice-label">Счёт-фактура</div>
      <div class="invoice-number">${invoiceNumber}</div>
      <div class="invoice-dates">
        Дата выставления: ${date}<br/>
        Дата доставки: ${deliveredDate}
      </div>
      <div style="margin-top:6px"><span class="status-delivered">✓ ДОСТАВЛЕНО</span></div>
    </div>
  </div>

  <!-- Parties -->
  <div class="parties">
    <div class="party-box">
      <div class="party-title">Поставщик</div>
      <div class="party-name">Wilsun</div>
      <div class="party-detail">
        Кыргызская Республика<br/>
        г. Бишкек
      </div>
    </div>
    <div class="party-box">
      <div class="party-title">Покупатель</div>
      <div class="party-name">${order.shop?.name ?? "—"}</div>
      <div class="party-detail">
        ${order.shop?.address ? order.shop.address + "<br/>" : ""}
        ${order.shop?.phone ? "Тел: " + order.shop.phone + "<br/>" : ""}
        ${order.shop?.owner ? "Владелец: " + order.shop.owner.firstName + " " + (order.shop.owner.lastName ?? "") : ""}
      </div>
    </div>
  </div>

  <!-- Info strip -->
  <div class="info-strip">
    <div class="info-chip">
      <div class="info-chip-label">Агент</div>
      <div class="info-chip-value">${order.agent ? order.agent.firstName + " " + (order.agent.lastName ?? "") : "—"}</div>
    </div>
    <div class="info-chip">
      <div class="info-chip-label">Тип оплаты</div>
      <div class="info-chip-value">${paymentLabel}</div>
    </div>
    <div class="info-chip">
      <div class="info-chip-label">${order.dueDate ? "Срок погашения" : "Оплата"}</div>
      <div class="info-chip-value">${order.dueDate ? new Date(order.dueDate).toLocaleDateString("ru-RU") : "При получении"}</div>
    </div>
  </div>

  <!-- Items table -->
  <table class="items-table">
    <thead>
      <tr>
        <th class="tc" style="width:36px">№</th>
        <th>Наименование товара</th>
        <th class="tc" style="width:50px">Ед.</th>
        <th class="tr" style="width:70px">Кол-во</th>
        <th class="tr" style="width:100px">Цена (сом)</th>
        <th class="tr" style="width:110px">Сумма (сом)</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  </table>

  <!-- Totals -->
  <div class="totals-section">
    <div class="totals-box">
      <div class="totals-row">
        <span class="totals-label">Итого позиций:</span>
        <span>${items.length}</span>
      </div>
      <div class="totals-row">
        <span class="totals-label">Итого:</span>
        <span>${fmt(totalAmount)} сом</span>
      </div>
    </div>
  </div>

  <!-- Amount in words -->
  <div class="amount-words">
    Итого к оплате: <span>${fmt(totalAmount)} сом</span><br/>
    <span>${amountInWords}</span>
  </div>

  <!-- Signatures -->
  <div class="signatures">
    <div class="sig-block">
      <div class="sig-title">Поставщик</div>
      <div class="sig-line">Подпись / Печать: ____________________</div>
    </div>
    <div class="sig-block">
      <div class="sig-title">Покупатель</div>
      <div class="sig-line">Подпись / Печать: ____________________</div>
    </div>
  </div>

  <!-- Footer -->
  <div class="footer">
    Счёт-фактура сформирован автоматически системой Wilsun &nbsp;·&nbsp;
    ${invoiceNumber} &nbsp;·&nbsp; ${deliveredDate}
  </div>

</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

// ── Simple Russian number-to-words ────────────────────────────────────────────
function rubles(amount: number): string {
  const int = Math.floor(amount);
  const kop = Math.round((amount - int) * 100);
  return `${numberToWords(int)} сом ${kop.toString().padStart(2, "0")} тыйын`;
}

function numberToWords(n: number): string {
  if (n === 0) return "ноль";
  const ones = [
    "",
    "один",
    "два",
    "три",
    "четыре",
    "пять",
    "шесть",
    "семь",
    "восемь",
    "девять",
    "десять",
    "одиннадцать",
    "двенадцать",
    "тринадцать",
    "четырнадцать",
    "пятнадцать",
    "шестнадцать",
    "семнадцать",
    "восемнадцать",
    "девятнадцать",
  ];
  const tens = [
    "",
    "",
    "двадцать",
    "тридцать",
    "сорок",
    "пятьдесят",
    "шестьдесят",
    "семьдесят",
    "восемьдесят",
    "девяносто",
  ];
  const hundreds = [
    "",
    "сто",
    "двести",
    "триста",
    "четыреста",
    "пятьсот",
    "шестьсот",
    "семьсот",
    "восемьсот",
    "девятьсот",
  ];
  const onesF = [
    "",
    "одна",
    "две",
    "три",
    "четыре",
    "пять",
    "шесть",
    "семь",
    "восемь",
    "девять",
  ];

  const parts: string[] = [];

  const billions = Math.floor(n / 1_000_000_000);
  if (billions)
    parts.push(
      chunk(billions, false) +
        " " +
        pluralize(billions, "миллиард", "миллиарда", "миллиардов"),
    );

  const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
  if (millions)
    parts.push(
      chunk(millions, false) +
        " " +
        pluralize(millions, "миллион", "миллиона", "миллионов"),
    );

  const thousands = Math.floor((n % 1_000_000) / 1_000);
  if (thousands)
    parts.push(
      chunkF(thousands) +
        " " +
        pluralize(thousands, "тысяча", "тысячи", "тысяч"),
    );

  const remainder = n % 1_000;
  if (remainder) parts.push(chunk(remainder, false));

  return parts.join(" ").trim();

  function chunk(num: number, _fem: boolean): string {
    const h = Math.floor(num / 100);
    const t = num % 100;
    const res: string[] = [];
    if (h) res.push(hundreds[h]);
    if (t < 20) {
      if (t) res.push(ones[t]);
    } else {
      res.push(tens[Math.floor(t / 10)]);
      if (t % 10) res.push(ones[t % 10]);
    }
    return res.join(" ");
  }

  function chunkF(num: number): string {
    const h = Math.floor(num / 100);
    const t = num % 100;
    const res: string[] = [];
    if (h) res.push(hundreds[h]);
    if (t < 20) {
      if (t) res.push(ones[t]);
    } else {
      res.push(tens[Math.floor(t / 10)]);
      if (t % 10) res.push(onesF[t % 10]);
    }
    return res.join(" ");
  }
}

function pluralize(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
